import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  ClipboardText,
  Exam,
  XCircle,
} from '@phosphor-icons/react';
import type { Question, Quiz, QuizResult } from '@cvs/shared';
import { api, type AssessmentRow } from '../lib/api';
import { useCourse } from '../lib/course';
import { useAsync } from '../lib/useAsync';
import { pct } from '../lib/format';
import {
  Button,
  Card,
  cx,
  EmptyState,
  ErrorState,
  LoadingState,
  Pill,
  ProgressBar,
  ProgressRing,
  SectionHeading,
  Tabs,
} from '../components/ui';

const PASS_PERCENT = 70;

export function Assessments() {
  const { quizId } = useParams();
  if (quizId) return <QuizRunner quizId={quizId} />;
  return <AssessmentList />;
}

// ── Mode A: knowledge checks (quizzes) + practice questions ──────────────────
function AssessmentList() {
  const [tab, setTab] = useState<'quizzes' | 'practice'>('quizzes');
  return (
    <div className="space-y-6">
      <SectionHeading
        eyebrow="Assessments and Practice"
        title="Assessments and Practice"
        description="Scored knowledge checks and interview-style practice questions across the whole course."
      />
      <Tabs
        items={[
          { id: 'quizzes', label: 'Knowledge checks', icon: ClipboardText },
          { id: 'practice', label: 'Practice questions', icon: Exam },
        ]}
        active={tab}
        onChange={(id) => setTab(id as 'quizzes' | 'practice')}
      />
      {tab === 'quizzes' ? <KnowledgeChecks /> : <PracticeQuestions />}
    </div>
  );
}

function KnowledgeChecks() {
  const s = useAsync(() => api.assessments(), []);
  if (s.loading) return <LoadingState />;
  if (s.error || !s.data) return <ErrorState message={s.error ?? 'Failed to load'} onRetry={s.reload} />;
  const rows = s.data;
  return rows.length === 0 ? (
    <EmptyState
      icon={ClipboardText}
      title="No knowledge checks yet"
      description="Scored quizzes appear here as your instructor publishes each class. Nothing to do yet — check back as the course opens up."
    />
  ) : (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((row) => (
        <AssessmentCard key={row.quiz.id} row={row} />
      ))}
    </div>
  );
}

function PracticeQuestions() {
  const { coursePath } = useCourse();
  const s = useAsync(() => api.practice(), []);
  if (s.loading) return <LoadingState />;
  if (s.error || !s.data) return <ErrorState message={s.error ?? 'Failed to load'} onRetry={s.reload} />;
  const rows = s.data;
  return (
    <div className="space-y-4">
      <p className="max-w-[70ch] text-sm leading-relaxed text-slate-600">
        Practice questions help you explain concepts in your own words. They are not graded. Save an answer before
        revealing the suggested answer.
      </p>
      {rows.length === 0 ? (
        <EmptyState
          icon={Exam}
          title="No practice questions yet"
          description="Interview-style practice appears here as your instructor adds it to classes."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((c) => (
            <Card key={c.moduleId} className="flex flex-col p-5">
              {c.sectionTitle && <span className="label-mono">{c.sectionTitle}</span>}
              <h3 className="mt-2 text-lg font-bold text-brand-navy">{c.title}</h3>
              <p className="mt-1 text-sm text-slate-500">
                {c.questionCount} interview-style question{c.questionCount === 1 ? '' : 's'}
              </p>
              <div className="mt-3">
                {c.answeredCount > 0 ? (
                  <Pill tone="teal">
                    {c.answeredCount} of {c.questionCount} answers saved
                  </Pill>
                ) : (
                  <Pill tone="slate">Not started</Pill>
                )}
              </div>
              <div className="mt-5 flex-1" />
              <Link to={`${coursePath(`classes/${c.moduleId}`)}?tab=practice`} className="btn-primary mt-1 self-start">
                Practise <ArrowRight size={18} weight="bold" />
              </Link>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function AssessmentCard({ row }: { row: AssessmentRow }) {
  const { coursePath } = useCourse();
  const { quiz, moduleTitle, questionCount, bestPercent } = row;

  return (
    <Card className="flex flex-col p-5">
      <span className="label-mono">{moduleTitle}</span>

      <h3 className="mt-2 text-lg font-bold text-brand-navy">{quiz.title}</h3>
      <p className="mt-1 text-sm text-slate-500">{questionCount} questions</p>

      <div className="mt-4">
        {bestPercent != null ? (
          <Pill tone={bestPercent >= PASS_PERCENT ? 'green' : 'amber'}>Best {pct(bestPercent)}</Pill>
        ) : (
          <Pill tone="slate">Not attempted</Pill>
        )}
      </div>

      <div className="mt-5 flex-1" />

      <Link to={coursePath(`assessments/${quiz.id}`)} className="btn-primary mt-1 self-start">
        Start <ArrowRight size={18} weight="bold" />
      </Link>
    </Card>
  );
}

// ── Mode B: quiz-taking flow ──────────────────────────────────────────────────
function QuizRunner({ quizId }: { quizId: string }) {
  const s = useAsync(() => api.quiz(quizId), [quizId]);

  if (s.loading) return <LoadingState label="Loading quiz…" />;
  if (s.error || !s.data) return <ErrorState message={s.error ?? 'Failed to load'} onRetry={s.reload} />;

  if (s.data.questions.length === 0) {
    return (
      <div className="space-y-6">
        <BackLink />
        <EmptyState icon={ClipboardText} title="This quiz has no questions yet" description="Check back soon." />
      </div>
    );
  }

  return <QuizFlow quiz={s.data.quiz} questions={s.data.questions} quizId={quizId} />;
}

function BackLink() {
  const { coursePath } = useCourse();
  return (
    <Link to={coursePath('assessments')} className="btn-ghost -ml-2 self-start text-sm">
      <ArrowLeft size={18} weight="bold" /> Back to assessments
    </Link>
  );
}

function QuizFlow({ quiz, questions, quizId }: { quiz: Quiz; questions: Question[]; quizId: string }) {
  const total = questions.length;
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<number[]>(() => Array<number>(total).fill(-1));
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<QuizResult | null>(null);

  const question = questions[index];
  const selected = answers[index];
  const hasSelection = selected >= 0;
  const isLast = index === total - 1;

  function select(optionIndex: number) {
    if (checked) return;
    setAnswers((prev) => {
      const next = [...prev];
      next[index] = optionIndex;
      return next;
    });
  }

  function check() {
    if (hasSelection) setChecked(true);
  }

  async function advance() {
    setSubmitError(null);
    if (!isLast) {
      setIndex((i) => i + 1);
      setChecked(false);
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.submitQuiz(quizId, answers);
      setResult(res.result);
    } catch (e: unknown) {
      setSubmitError(e instanceof Error ? e.message : 'Could not submit your answers');
    } finally {
      setSubmitting(false);
    }
  }

  function retake() {
    setAnswers(Array<number>(total).fill(-1));
    setIndex(0);
    setChecked(false);
    setResult(null);
    setSubmitError(null);
  }

  if (result) return <Results result={result} onRetake={retake} title={quiz.title} />;

  const progress = ((index + (checked ? 1 : 0)) / total) * 100;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3">
        <BackLink />
        <SectionHeading eyebrow={quiz.title} title={`Question ${index + 1} of ${total}`} />
      </div>

      <Card className="p-6">
        <ProgressBar value={progress} className="mb-6" />

        <fieldset>
          <legend className="text-lg font-semibold text-brand-navy">{question.prompt}</legend>

          <div className="mt-5 space-y-3" role="radiogroup" aria-label="Answer options">
            {question.options.map((option, oi) => (
              <OptionButton
                key={oi}
                option={option}
                isSelected={selected === oi}
                isCorrect={oi === question.correctIndex}
                checked={checked}
                onSelect={() => select(oi)}
              />
            ))}
          </div>
        </fieldset>

        {checked && (
          <div className="mt-5 rounded-xl bg-slate-50 p-4">
            <p className="text-sm font-semibold text-brand-navy">
              {selected === question.correctIndex ? 'Correct' : 'Not quite'}
            </p>
            <p className="mt-1 text-sm text-slate-600">{question.explanation}</p>
          </div>
        )}

        {submitError && <p className="mt-4 text-sm font-semibold text-rose-600">{submitError}</p>}

        <div className="mt-6 flex justify-end gap-3">
          {!checked ? (
            <Button onClick={check} disabled={!hasSelection}>
              Check
            </Button>
          ) : (
            <Button onClick={advance} loading={submitting} icon={isLast ? undefined : ArrowRight}>
              {isLast ? 'See results' : 'Next'}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}

function OptionButton({
  option,
  isSelected,
  isCorrect,
  checked,
  onSelect,
}: {
  option: string;
  isSelected: boolean;
  isCorrect: boolean;
  checked: boolean;
  onSelect: () => void;
}) {
  const showCorrect = checked && isCorrect;
  const showWrong = checked && isSelected && !isCorrect;

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      disabled={checked}
      onClick={onSelect}
      className={cx(
        'flex w-full items-center justify-between gap-3 rounded-xl border-2 px-4 py-3 text-left text-sm font-medium transition',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal focus-visible:ring-offset-2',
        showCorrect && 'border-emerald-400 bg-emerald-50 text-emerald-800',
        showWrong && 'border-rose-400 bg-rose-50 text-rose-800',
        !showCorrect && !showWrong && isSelected && 'border-brand-teal bg-teal-50 text-brand-navy',
        !showCorrect && !showWrong && !isSelected && 'border-slate-200 text-slate-700 hover:border-slate-300',
        checked && 'cursor-default',
      )}
    >
      <span>{option}</span>
      {showCorrect && <CheckCircle size={20} weight="fill" className="shrink-0 text-emerald-600" aria-hidden="true" />}
      {showWrong && <XCircle size={20} weight="fill" className="shrink-0 text-rose-600" aria-hidden="true" />}
    </button>
  );
}

function Results({ result, onRetake, title }: { result: QuizResult; onRetake: () => void; title: string }) {
  const { coursePath } = useCourse();
  const percent = useMemo(() => Math.round(result.percent), [result.percent]);

  return (
    <div className="space-y-6">
      <SectionHeading eyebrow={title} title="Your results" />

      <Card className="flex flex-col items-center gap-5 p-8 text-center">
        <ProgressRing value={percent} size={160} stroke={14}>
          <span className="text-4xl font-extrabold text-brand-navy">{percent}%</span>
          <span className="text-xs text-slate-500">
            {result.correct}/{result.total} correct
          </span>
        </ProgressRing>

        {result.passed ? (
          <Pill tone="green">Passed</Pill>
        ) : (
          <Pill tone="rose">Keep practising — 70% to pass</Pill>
        )}

        <p className="max-w-md text-sm text-slate-500">
          {result.passed
            ? 'Great work — you have cleared this assessment.'
            : 'Review the explanations and give it another go when you are ready.'}
        </p>

        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button onClick={onRetake}>Retake</Button>
          <Link to={coursePath('assessments')} className="btn-secondary">
            <ArrowLeft size={18} weight="bold" /> Back to assessments
          </Link>
        </div>
      </Card>
    </div>
  );
}
